import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Contact, Plus, Search, Pencil, Trash2, Mail, Phone, ChevronRight, BadgeCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const EMPTY = {
  nome: "", cognome: "", email: "", telefono: "", tipo_documento: "", numero_documento: "",
  indirizzo: "", citta: "", cap: "", ragione_sociale: "", piva: "", codice_fiscale: "",
  consenso_privacy: false, consenso_marketing: false, sede_preferita: "", note: "",
};

export const CustomerForm = ({ form, setForm }) => {
  const [sedi, setSedi] = useState([]);
  useEffect(() => { api.get("/sedi").then((r) => setSedi(r.data)).catch(() => {}); }, []);
  return (
  <div className="space-y-4 py-2">
    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Nome *</Label>
        <Input data-testid="cust-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Cognome *</Label>
        <Input data-testid="cust-cognome" value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Email *</Label>
        <Input data-testid="cust-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Telefono</Label>
        <Input data-testid="cust-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Sede preferita</Label>
        <Select value={form.sede_preferita || "none"} onValueChange={(v) => setForm({ ...form, sede_preferita: v === "none" ? "" : v })}>
          <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="cust-sede-preferita"><SelectValue placeholder="Nessuna" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-zinc-800"><SelectItem value="none">Nessuna</SelectItem>{sedi.map((s) => <SelectItem key={s.id} value={s.id}>{s.nome}</SelectItem>)}</SelectContent>
        </Select>
      </div>
    </div>

    <div className="grid sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Tipo documento</Label>
        <Select value={form.tipo_documento || ""} onValueChange={(v) => setForm({ ...form, tipo_documento: v })}>
          <SelectTrigger className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" data-testid="cust-tipodoc"><SelectValue placeholder="Seleziona" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-zinc-800">
            <SelectItem value="carta_identita">Carta d'identità</SelectItem>
            <SelectItem value="patente">Patente</SelectItem>
            <SelectItem value="passaporto">Passaporto</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Numero documento</Label>
        <Input data-testid="cust-numdoc" value={form.numero_documento} onChange={(e) => setForm({ ...form, numero_documento: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
    </div>

    <div className="grid sm:grid-cols-4 gap-4">
      <div className="sm:col-span-2">
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Indirizzo</Label>
        <Input data-testid="cust-indirizzo" value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">Città</Label>
        <Input data-testid="cust-citta" value={form.citta} onChange={(e) => setForm({ ...form, citta: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-zinc-500">CAP</Label>
        <Input data-testid="cust-cap" value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
      </div>
    </div>

    <div className="border-t border-zinc-800 pt-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-3">Dati per export amministrativo (opzionali)</div>
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Ragione sociale</Label>
          <Input data-testid="cust-ragsoc" value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">P.IVA</Label>
          <Input data-testid="cust-piva" value={form.piva} onChange={(e) => setForm({ ...form, piva: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">Codice Fiscale</Label>
          <Input data-testid="cust-cf" value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
      </div>
    </div>

    <div>
      <Label className="text-xs uppercase tracking-wider text-zinc-500">Note interne</Label>
      <Textarea data-testid="cust-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-zinc-800 rounded-sm" rows={2} />
    </div>

    <div className="space-y-2 border-t border-zinc-800 pt-4">
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox checked={form.consenso_privacy} onCheckedChange={(v) => setForm({ ...form, consenso_privacy: !!v })} data-testid="cust-privacy" className="mt-0.5" />
        <span className="text-sm text-zinc-300">Consenso privacy (trattamento dati) <span className="text-[#FF3B30]">*obbligatorio</span></span>
      </label>
      <label className="flex items-start gap-2 cursor-pointer">
        <Checkbox checked={form.consenso_marketing} onCheckedChange={(v) => setForm({ ...form, consenso_marketing: !!v })} data-testid="cust-marketing" className="mt-0.5" />
        <span className="text-sm text-zinc-300">Consenso marketing (mailing list, facoltativo)</span>
      </label>
    </div>
  </div>
  );
};

export default function Customers() {
  const { hasPerm } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("tutti");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const res = await api.get("/customers");
    setCustomers(res.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c, e) => {
    e?.stopPropagation();
    setEditing(c);
    setForm({ ...EMPTY, ...c });
    setOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim() || !form.cognome.trim()) { toast.error("Nome e cognome obbligatori"); return; }
    if (!form.email.trim()) { toast.error("Email obbligatoria"); return; }
    if (!form.consenso_privacy) { toast.error("Il consenso privacy è obbligatorio"); return; }
    setSaving(true);
    try {
      if (editing) { await api.put(`/customers/${editing.id}`, form); toast.success("Cliente aggiornato"); }
      else { await api.post("/customers", form); toast.success("Cliente creato"); }
      setOpen(false); await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const del = async (c) => {
    try { await api.delete(`/customers/${c.id}`); toast.success("Cliente eliminato"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = customers.filter((c) => {
    const matchQ = [c.nome, c.cognome, c.email, c.telefono, c.ragione_sociale].join(" ").toLowerCase().includes(query.toLowerCase());
    const matchF = filter === "tutti" ? true : filter === "marketing" ? c.consenso_marketing : !c.consenso_marketing;
    return matchQ && matchF;
  });

  return (
    <div className="p-5 sm:p-8 max-w-7xl mx-auto fade-in">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1">Anagrafica noleggiatori</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight">CLIENTI</h1>
        </div>
        {hasPerm("customers.create") && (
          <button onClick={openNew} data-testid="add-customer-btn"
            className="flex items-center gap-2 rounded-sm bg-[#f98125] text-white font-bold uppercase tracking-wide px-5 py-3 hover:bg-[#d96a12] transition-colors active:scale-95">
            <Plus className="h-5 w-5" /> Nuovo cliente
          </button>
        )}
      </div>

      <div className="flex gap-3 mb-6 flex-wrap items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
          <Input data-testid="customer-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca per nome, email, telefono..." className="pl-9 bg-[#0A0A0A] border-zinc-800 rounded-sm" />
        </div>
        <div className="flex gap-2">
          {[["tutti", "Tutti"], ["marketing", "Iscritti newsletter"], ["no_marketing", "No newsletter"]].map(([k, lbl]) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`cust-filter-${k}`}
              className={`px-3 py-2 rounded-sm text-sm border transition-colors ${filter === k ? "border-[#f98125] bg-[#f98125]/10 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"}`}>{lbl}</button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-zinc-600 text-sm py-16 text-center border border-dashed border-zinc-800 rounded-sm">
          {customers.length === 0 ? "Nessun cliente registrato. Aggiungine uno con \"Nuovo cliente\"." : "Nessun risultato."}
        </div>
      ) : (
        <div className="bg-[#141414] border border-zinc-800 rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs font-bold text-zinc-500 uppercase tracking-wider border-b border-zinc-800">
                  <th className="text-left px-4 py-3">Cliente</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Contatti</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Documento</th>
                  <th className="text-center px-4 py-3">Newsletter</th>
                  <th className="text-right px-4 py-3">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr key={c.id} data-testid={`customer-row-${c.id}`} onClick={() => navigate(`/clienti/${c.id}`)}
                    className="border-b border-zinc-800/50 hover:bg-[#1a1a1a] transition-colors cursor-pointer">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-sm bg-[#f98125]/15 flex items-center justify-center text-[#f98125] font-bold uppercase">
                          {(c.nome?.[0] || "") + (c.cognome?.[0] || "")}
                        </div>
                        <div>
                          <div className="font-medium flex items-center gap-1.5">{c.nome} {c.cognome} <ChevronRight className="h-3.5 w-3.5 text-zinc-600" /></div>
                          {c.ragione_sociale && <div className="text-xs text-zinc-500">{c.ragione_sociale}</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell text-zinc-400">
                      <div className="flex items-center gap-1 text-xs"><Mail className="h-3 w-3" /> {c.email}</div>
                      {c.telefono && <div className="flex items-center gap-1 text-xs mt-0.5"><Phone className="h-3 w-3" /> {c.telefono}</div>}
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-zinc-500 text-xs">{c.numero_documento || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      {c.consenso_marketing
                        ? <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#39FF14]"><BadgeCheck className="h-3.5 w-3.5" /> Sì</span>
                        : <span className="text-[10px] uppercase tracking-wider text-zinc-600">No</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {hasPerm("customers.edit") && (
                          <button onClick={(e) => openEdit(c, e)} data-testid={`edit-customer-${c.id}`} className="p-1.5 text-zinc-500 hover:text-white hover:bg-zinc-800 rounded-sm"><Pencil className="h-4 w-4" /></button>
                        )}
                        {hasPerm("customers.delete") && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <button onClick={(e) => e.stopPropagation()} data-testid={`delete-customer-${c.id}`} className="p-1.5 text-zinc-500 hover:text-[#FF3B30] hover:bg-zinc-800 rounded-sm"><Trash2 className="h-4 w-4" /></button>
                            </AlertDialogTrigger>
                            <AlertDialogContent className="bg-[#141414] border-zinc-800" onClick={(e) => e.stopPropagation()}>
                              <AlertDialogHeader><AlertDialogTitle>Eliminare {c.nome} {c.cognome}?</AlertDialogTitle><AlertDialogDescription>Azione irreversibile.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel className="rounded-sm">Annulla</AlertDialogCancel><AlertDialogAction onClick={() => del(c)} className="rounded-sm bg-[#FF3B30] hover:bg-[#cc2f26]">Elimina</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#141414] border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tight flex items-center gap-2"><Contact className="h-5 w-5 text-[#f98125]" /> {editing ? "Modifica cliente" : "Nuovo cliente"}</DialogTitle>
          </DialogHeader>
          <CustomerForm form={form} setForm={setForm} />
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="rounded-sm border border-zinc-700 px-5 py-2.5 hover:bg-zinc-800 transition-colors">Annulla</button>
            <button onClick={save} disabled={saving} data-testid="customer-save" className="rounded-sm bg-[#f98125] text-white font-bold px-5 py-2.5 hover:bg-[#d96a12] transition-colors disabled:opacity-50">{saving ? "Salvataggio..." : "Salva"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
